import { CategoryTreeAPIResponse, CategoryTreePostSummary, HelpCategoryGroup, LocalizedText, Post } from './type';

export function getLocalizedValue(value: LocalizedText | undefined, lang: 'en' | 'vi'): string {
	if (!value) {
		return '';
	}

	return value[lang] || value.en || value.vi || '';
}

export function groupPostsByCategory(posts: Post[]): HelpCategoryGroup[] {
	const categoryMap = new Map<number, HelpCategoryGroup>();

	posts.forEach((post) => {
		const existingGroup = categoryMap.get(post.category.id);

		if (existingGroup) {
			existingGroup.articles.push(post);
			return;
		}

		categoryMap.set(post.category.id, {
			id: `cat-${post.category.id}`,
			categoryId: post.category.id,
			slug: post.category.slug,
			title: post.category.name,
			articles: [post],
		});
	});

	return Array.from(categoryMap.values()).sort((left, right) => {
		const leftOrder = left.articles[0]?.category.order ?? 0;
		const rightOrder = right.articles[0]?.category.order ?? 0;
		return leftOrder - rightOrder;
	});
}

export function mapCategoryTreeToGroups(categories: CategoryTreeAPIResponse[], posts: Post[]): HelpCategoryGroup[] {
	const postsByCategoryId = new Map<number, Post[]>();

	posts.forEach((post) => {
		const existingPosts = postsByCategoryId.get(post.category_id) || [];
		existingPosts.push(post);
		postsByCategoryId.set(post.category_id, existingPosts);
	});

	const groups: HelpCategoryGroup[] = [];

	const visitCategory = (category: CategoryTreeAPIResponse) => {
		const articles = (postsByCategoryId.get(category.id) || []).slice().sort((left, right) => {
			return left.id - right.id;
		});

		if (articles.length > 0) {
			groups.push({
				id: `cat-${category.id}`,
				categoryId: category.id,
				slug: category.slug,
				title: category.name,
				articles,
			});
		}

		category.children?.forEach(visitCategory);
	};

	categories
		.slice()
		.sort((left, right) => left.order - right.order)
		.forEach(visitCategory);

	return groups;
}

export function mapCategoryTreeToPreviewGroups(categories: CategoryTreeAPIResponse[]): HelpCategoryGroup<CategoryTreePostSummary>[] {
	const groups: HelpCategoryGroup<CategoryTreePostSummary>[] = [];

	const visitCategory = (category: CategoryTreeAPIResponse) => {
		const articles = (category.posts || []).slice().sort((left, right) => left.id - right.id);

		if (articles.length > 0) {
			groups.push({
				id: `cat-${category.id}`,
				categoryId: category.id,
				slug: category.slug,
				title: category.name,
				articles,
			});
		}

		category.children
			?.slice()
			.sort((left, right) => left.order - right.order)
			.forEach(visitCategory);
	};

	categories
		.slice()
		.sort((left, right) => left.order - right.order)
		.forEach(visitCategory);

	return groups;
}

export function findPostInCategoryTreeBySlug(
	categories: CategoryTreeAPIResponse[],
	targetSlug: string,
): CategoryTreePostSummary | null {
	for (const category of categories) {
		const matchingPost = category.posts?.find((post) => post.slug === targetSlug);

		if (matchingPost) {
			return matchingPost;
		}

		const matchingChildPost = findPostInCategoryTreeBySlug(category.children || [], targetSlug);

		if (matchingChildPost) {
			return matchingChildPost;
		}
	}

	return null;
}

export function findCategoryPathIds(categories: CategoryTreeAPIResponse[], targetId: number): string[] {
	for (const category of categories) {
		if (category.id === targetId) {
			return [`cat-${category.id}`];
		}

		const childPath = findCategoryPathIds(category.children || [], targetId);

		if (childPath.length > 0) {
			return [`cat-${category.id}`, ...childPath];
		}
	}

	return [];
}

export async function loadAllHelpPosts(getAllPosts: (page?: number, perPage?: number) => Promise<{ data: Post[]; last_page: number; }>): Promise<Post[]> {
	const firstPage = await getAllPosts(1, 100);
	const allPosts = [...firstPage.data];

	if (firstPage.last_page <= 1) {
		return allPosts;
	}

	for (let page = 2; page <= firstPage.last_page; page += 1) {
		const response = await getAllPosts(page, 100);
		allPosts.push(...response.data);
	}

	return allPosts;
}
